import { Inject, Service } from "typedi";
import { DomMethods } from "../../../application/core/shared/dom-methods/DomMethods";
import { IGooglePayPaymentRequest } from "../../../integrations/google-pay/models/IGooglePayPaymentRequest";
import { IGooglePaySessionPaymentsClient } from "../../../integrations/google-pay/models/IGooglePaySessionPaymentsClient";
import { IConfig } from "../../../shared/model/config/IConfig";
import { type IInternalsMonitor } from "../../../application/core/services/monitoring/IInternalsMonitor";
import { GooglePayConfigMapper } from "./GooglePayConfigMapper";
import { GooglePayPaymentService } from "./GooglePayPaymentService";
import { GooglePayPrePaymentCallbackHandler } from "./GooglePayPrePaymentCallbackHandler";
import { GooglePayJwtPriceResolver } from "./GooglePayJwtPriceResolver";
import { ConfigProvider } from "../../../shared/services/config-provider/ConfigProvider";

export enum GooglePayButtonActionResult {
  SUCCESS = "SUCCESS",
  CANCELED = "CANCELED",
  ERROR = "ERROR",
}

@Service()
export class GooglePayButtonAction {
  constructor(
    private configMapper: GooglePayConfigMapper,
    private prePaymentCallbackHandler: GooglePayPrePaymentCallbackHandler,
    private jwtPriceResolver: GooglePayJwtPriceResolver,
    private googlePayPaymentService: GooglePayPaymentService,
    private configProvider: ConfigProvider,
    @Inject("IInternalsMonitor") protected internalMonitor: IInternalsMonitor,
  ) {}

  async execute(
    googlePaySdk: IGooglePaySessionPaymentsClient,
  ): Promise<GooglePayButtonActionResult> {
    const config = this.configProvider.getConfig();
    let paymentDataRequest = this.configMapper.createPaymentDataRequest(config);

    paymentDataRequest = this.applyJwtPrice(paymentDataRequest, config.jwt);

    if (this.prePaymentCallbackHandler.isRequired(paymentDataRequest)) {
      paymentDataRequest =
        await this.prePaymentCallbackHandler.trigger(paymentDataRequest);
    }

    return this.executePayment(config, googlePaySdk, paymentDataRequest);
  }

  /**
   * Ensures the payment request price matches the JWT. Merchants can call
   * updateJwt at any point before payment — this guarantees the wallet sheet
   * always reflects the current JWT price, even without callbacks.
   */
  private applyJwtPrice(
    request: IGooglePayPaymentRequest,
    jwt: string,
  ): IGooglePayPaymentRequest {
    const jwtPrice = this.jwtPriceResolver.resolve(jwt);

    if (!jwtPrice) {
      return request;
    }

    return {
      ...request,
      transactionInfo: {
        ...request.transactionInfo,
        totalPrice: jwtPrice.totalPrice,
        currencyCode: jwtPrice.currencyCode,
      },
    };
  }

  private async executePayment(
    config: IConfig,
    googlePaySdk: IGooglePaySessionPaymentsClient,
    request: IGooglePayPaymentRequest,
  ): Promise<GooglePayButtonActionResult> {
    try {
      const paymentData = await googlePaySdk.loadPaymentData(request);
      this.handleOutcome(config, "success", paymentData);
      return GooglePayButtonActionResult.SUCCESS;
    } catch (error: unknown) {
      if (this.isCancellation(error)) {
        this.handleOutcome(config, "cancel");
        return GooglePayButtonActionResult.CANCELED;
      }

      this.handleOutcome(config, "error");
      return GooglePayButtonActionResult.ERROR;
    }
  }

  private handleOutcome(
    config: IConfig,
    outcome: "success" | "cancel" | "error",
    paymentData?: google.payments.api.PaymentData,
  ): void {
    const formData = DomMethods.parseForm(config.formId);

    switch (outcome) {
      case "success":
        return this.googlePayPaymentService.processPayment(
          formData,
          paymentData,
        );
      case "cancel":
        return this.googlePayPaymentService.cancelPayment(formData);
      case "error":
        return this.googlePayPaymentService.errorPayment(formData);
    }
  }

  private isCancellation(error: unknown): boolean {
    return (
      typeof error === "object" &&
      error !== null &&
      "statusCode" in error &&
      (error as { statusCode: string }).statusCode === "CANCELED"
    );
  }
}
